import {
  FiltersProvider,
  SearchInput,
  FilterData,
  FilterChipSelect,
  FilterValue,
  FilterChipDateRange,
} from '@nexelem/filters';
import { ProLightCalendarRange } from '@nexelem/icons/ProLightCalendarRange';
import { useGetAllQueuesAndAreas } from '../../../../hooks';
import { useCustomTranslation } from '../../../../../../../../hooks/useCustomTranslation';
import { TextField, Button } from '@nexelem/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { useCallback } from 'react';
import { isEqual } from 'lodash';

export type SimulationFiltersData = {
  search: string;
  queueId: number[];
  queueAreaId: number[];
  plannedDateRange: { from?: Date; to?: Date };
};

type ExtendFilterValue = FilterValue & { color: string };

type SimulationFiltersProps = {
  filters: FilterData<SimulationFiltersData>;
  onChange: (filters: FilterData<SimulationFiltersData>) => void;
};

export const defaultSimulationFilters: FilterData<SimulationFiltersData> = {
  search: { value: '', isMultiple: false, path: 'search' },
  queueId: { value: [], isMultiple: true, path: 'queueId' },
  queueAreaId: { value: [], isMultiple: true, path: 'queueAreaId' },
  plannedDateRange: { value: { from: undefined, to: undefined }, isMultiple: false, path: 'plannedDateRange' },
};

const renderAreaOptionWithColor = (option: FilterValue) => {
  const { color, key } = option as ExtendFilterValue;

  return (
    <div className="flex items-center gap-1">
      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: `#${color}` }} />
      {key}
    </div>
  );
};

export const SimulationModalFilters = ({ onChange, filters }: SimulationFiltersProps) => {
  const { tWrapper } = useCustomTranslation('products.simulationPlan');
  const { queueOptions, queueAreaOptions } = useGetAllQueuesAndAreas();

  const hasActiveFilters = Object.keys(defaultSimulationFilters).some((key) => {
    const filterKey = key as keyof SimulationFiltersData;
    return !isEqual(filters[filterKey]?.value, defaultSimulationFilters[filterKey].value);
  });

  const handleClearFilters = () => {
    onChange(defaultSimulationFilters);
  };

  const filterWorkstations = useCallback(
    (searchTerm: string) =>
      queueOptions.filter((option) => option.key?.toString().toLowerCase().includes(searchTerm.toLowerCase())),
    [queueOptions],
  );

  const onFetchFilteredWorkstations = useCallback(
    async (searchTerm: string): Promise<FilterValue[]> => Promise.resolve(filterWorkstations(searchTerm)),
    [filterWorkstations],
  );

  // temporary solution to allow only single select in queueId and queueAreaId filters
  // waiting for BE to implement multi-select
  const handleSingleSelectChange = (newFilters: FilterData<SimulationFiltersData>) => {
    const updatedFilters = { ...newFilters };

    if (updatedFilters.queueId?.value && Array.isArray(updatedFilters.queueId.value)) {
      const queueValues = updatedFilters.queueId.value;
      if (queueValues.length > 1) {
        updatedFilters.queueId = {
          ...updatedFilters.queueId,
          value: [queueValues[queueValues.length - 1]],
        };
      }
    }

    if (updatedFilters.queueAreaId?.value && Array.isArray(updatedFilters.queueAreaId.value)) {
      const areaValues = updatedFilters.queueAreaId.value;
      if (areaValues.length > 1) {
        updatedFilters.queueAreaId = {
          ...updatedFilters.queueAreaId,
          value: [areaValues[areaValues.length - 1]],
        };
      }
    }

    onChange(updatedFilters);
  };

  return (
    <FiltersProvider value={filters} onChange={handleSingleSelectChange}>
      {(register) => (
        <div className="flex w-full flex-wrap items-center gap-4">
          <FilterChipDateRange
            Icon={ProLightCalendarRange}
            label={tWrapper('filterPlannedDateRangeLabel')}
            path="plannedDateRange"
            register={register}
          />
          <FilterChipSelect
            isMultiple={true}
            label={tWrapper('filterAreaLabel')}
            options={queueAreaOptions}
            path="queueAreaId"
            register={register}
            renderOptionLabel={renderAreaOptionWithColor}
            zIndex="9999"
          />
          <FilterChipSelect
            fetchData={onFetchFilteredWorkstations}
            isMultiple={true}
            label={tWrapper('filterWorkstationLabel')}
            path="queueId"
            register={register}
            zIndex="9999"
            HeaderList={
              <TextField
                placeholder={tWrapper('filterSearchPlaceholder')}
                StartIcon={() => <FontAwesomeIcon icon={faMagnifyingGlass} />}
              />
            }
          />
          <SearchInput path="search" placeholder={tWrapper('filterSearchPlaceholder')} register={register} />
          {hasActiveFilters && (
            <Button variant="text" onClick={handleClearFilters}>
              {tWrapper('filterClearLabel')}
            </Button>
          )}
        </div>
      )}
    </FiltersProvider>
  );
};
